import { useTranslation } from 'react-i18next'
import { ChartLine, LayoutGrid } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

export interface StatTableRow {
  key: string
  label: string
  icon: LucideIcon
  /** Accent colour — pass an `--ad-color-*` token, as for a {@link StatTile}. */
  color: string
  /** The period's total. */
  value: string
  /** What the tile would say under its value — a rate, a share, an eCPM. */
  sub?: string | null
  /** Whether the metric is plotted in the chart. */
  selected?: boolean
}

interface StatTableProps {
  rows: StatTableRow[]
  onToggleChart?: (key: string) => void
  /** Moves the metric out of the table into a tile of its own. */
  onShowAsCard?: (key: string) => void
}

/**
 * The same metrics a {@link StatTile} shows, one per row, for the ones a user
 * would rather read in a list than give a tile of their own.
 */
export function StatTable({ rows, onToggleChart, onShowAsCard }: StatTableProps) {
  const hasActions = !!onToggleChart || !!onShowAsCard
  const { t } = useTranslation()

  if (rows.length === 0) return null

  return (
    <div className="mt-3 overflow-hidden rounded-lg border bg-(--ad-surface-raised)">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs">{t('stats.metric')}</TableHead>
            <TableHead className="text-right text-xs">{t('stats.total')}</TableHead>
            <TableHead className="text-xs">{t('stats.details')}</TableHead>
            {hasActions && (
              <TableHead className="w-0">
                <span className="sr-only">{t('stats.actions')}</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ key, label, icon: Icon, color, value, sub, selected }) => (
            <TableRow
              key={key}
              // The whole row plots the metric, like clicking a tile; the
              // button at its end is the same toggle for the keyboard.
              className={onToggleChart ? 'cursor-pointer' : undefined}
              style={
                selected
                  ? { background: `color-mix(in oklab, ${color} 8%, var(--card))` }
                  : undefined
              }
              onClick={onToggleChart ? () => onToggleChart(key) : undefined}
            >
              <TableCell className="text-xs">
                <span className="flex items-center gap-1.5">
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-md"
                    style={{ color, background: `color-mix(in oklab, ${color} 16%, transparent)` }}
                  >
                    <Icon className="size-3" />
                  </span>
                  <span
                    className={selected ? 'font-medium' : 'text-muted-foreground'}
                    style={selected ? { color } : undefined}
                  >
                    {label}
                  </span>
                </span>
              </TableCell>
              <TableCell className="text-right text-xs font-medium tabular-nums">{value}</TableCell>
              <TableCell className="text-xs text-muted-foreground tabular-nums">{sub}</TableCell>
              {hasActions && (
                <TableCell className="w-0 py-1 pr-2 pl-0">
                  <span className="flex items-center gap-0.5">
                    {onShowAsCard && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            aria-label={t('stats.showAsCard')}
                            className="text-muted-foreground"
                            onClick={(e) => {
                              e.stopPropagation()
                              onShowAsCard(key)
                            }}
                          >
                            <LayoutGrid />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('stats.showAsCard')}</TooltipContent>
                      </Tooltip>
                    )}
                    {onToggleChart && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            aria-label={t('stats.showInChart')}
                            aria-pressed={!!selected}
                            className={selected ? undefined : 'text-muted-foreground'}
                            // Filled with the line's colour, like a selected tile.
                            style={
                              selected ? { color: 'var(--card)', background: color } : undefined
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              onToggleChart(key)
                            }}
                          >
                            <ChartLine />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('stats.showInChart')}</TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
